import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import { DatePicker } from "@ui/datepicker/DatePicker";
import Input from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import { Spinner } from "@ui/spinner/Spinner";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Typo } from "@ui/text/Typo";
import { useWindowSize } from "@ui/utils/useResize";
import { isMobileViewport, MOBILE_VIEWPORT_MEDIA as MOBILE } from "@ui/utils/mobileViewport";
import { formatCurrencyWithNumberToSymbolWithNumber } from "@ui/utils/formatter";
import { FC, KeyboardEvent as ReactKeyboardEvent, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GD } from "src/GD";
import { USER_ROLE } from "src/services/AuthService";
import { createTransactionsCsv } from "src/utils/transactionCsv";
import { getFriendlyErrorMessage } from "src/utils/errors";
import {
  getFeeNotes,
  getTransactionAmountPresentation,
  getTransactionDisplayLabel,
  getTransactionParties,
  getTransactionTypeLabel,
  groupInternalTransfers,
  type TransactionListItem,
} from "src/utils/transactionPresentation";
import { InternalTransferDetails } from "./InternalTransferDetails";
import { useIsPrivateAccount } from "../private/PrivateAccountContext";
import { StatusBadge } from "./StatusBadge";
import { TransactionDetails } from "./TransactionDetails";
import { TransactionFilterPresets } from "./TransactionFilterPresets";
import { S_UNIVERSAL_DIALOG } from "./universalDialog/signals";
import { useOptionalIssuer } from "../issuer/IssuerProvider";
import { DEFAULT_CURRENCY } from "src/constants";
import { DS, StatGroup } from "../ds/Kit";

type TransactionsListRow = ICompanyTrxTransaction & { uid: string };

type TransactionFilterState = {
  search: string;
  cardUid: string;
  buyerUid: string;
  teamUid: string;
  cardMask: string;
  cardBin: string;
  direction: string;
  status: string;
  startDate: Date | null;
  endDate: Date | null;
  minAmount: string;
  maxAmount: string;
  currency: string;
  entity: string;
  txType: string;
};

const TRANSACTIONS_PAGE_LIMIT = 20;
const DEFAULT_LOOKBACK_MONTHS = 3;
const FILTER_DEBOUNCE_MS = 300;
const DOUBLE_DIGIT_PADDING = 2;
const CARD_LAST_FOUR_LENGTH = 4;
const ISO_DATE_LENGTH = 10;
const ELLIPSIS_SX = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;
const ADVANCED_FILTER_INPUT_SX = {
  width: "100%",
  gap: "0px",
  "& > div:first-child": { display: "none" },
} as const;
const ACCOUNT_HOLDER_SCOPE_LABELS: Record<TCompanyTransactionEntity, string> = {
  all: "All",
  buyer: "Buyers",
  card: "Cards",
  company: "Company",
};

const withDefaultFilterOption = (
  options: ICompanyTransactionFilterOption[] | undefined,
  defaultOption: ICompanyTransactionFilterOption,
) => {
  const normalizedOptions = Array.isArray(options) ? options : [];
  return normalizedOptions.some(
    (option) => option.value === defaultOption.value,
  )
    ? normalizedOptions
    : [defaultOption, ...normalizedOptions];
};

const toDateValue = (value: string | Date) => {
  const date = value instanceof Date ? value : value ? new Date(value) : undefined;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

const normalizeDigits = (value: string, maxLength?: number) => {
  const digits = value.replace(/\D/g, "");
  return typeof maxLength === "number" ? digits.slice(0, maxLength) : digits;
};

const getMaskedLastFour = (cardMask?: string) => {
  const digits = (cardMask || "").replace(/\D/g, "");
  const lastFour = digits.slice(-CARD_LAST_FOUR_LENGTH);
  return lastFour ? `•••• ${lastFour}` : "—";
};

const formatApiDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(DOUBLE_DIGIT_PADDING, "0");
  const day = String(date.getDate()).padStart(DOUBLE_DIGIT_PADDING, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });

const formatTime = (date: string) =>
  new Date(date).toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

const TransactionHeaderCell: FC<{
  children: string;
  align?: "left" | "right";
}> = ({ children, align = "left" }) => (
  <Typo
    sx={{
      color: DS.muted,
      fontSize: "12px",
      fontWeight: 500,
      textAlign: align,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </Typo>
);

const AdvancedFilterField: FC<{
  label: string;
  order: number;
  children: ReactNode;
}> = ({ label, order, children }) => (
  <VBox gap="6px" sx={{ minWidth: 0, order }}>
    <Typo sx={{ color: DS.muted, fontSize: "12px", fontWeight: 500 }}>
      {label}
    </Typo>
    {children}
  </VBox>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={DS.faint} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const DIRECTION_TILE_TONES = {
  income: { background: "#ECFDF5", color: "#047857", path: "M17 7 7 17M7 17h8M7 17V9" },
  expense: { background: "#FEF2F2", color: "#B42318", path: "M7 17 17 7M17 7H9M17 7v8" },
  transfer: { background: DS.accentSoft, color: DS.accentInk, path: "M7 10h11l-3-3M17 14H6l3 3" },
} as const;

const DirectionTile: FC<{ kind: "income" | "expense" | "transfer" }> = ({ kind }) => {
  const tone = DIRECTION_TILE_TONES[kind] ?? DIRECTION_TILE_TONES.transfer;
  return (
    <Box
      aria-hidden
      sx={{
        width: "38px",
        height: "38px",
        minWidth: "38px",
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: tone.background,
        color: tone.color,
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d={tone.path} />
      </svg>
    </Box>
  );
};

const ToolbarButton: FC<{
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  ariaExpanded?: boolean;
}> = ({ children, onClick, disabled, active, ariaExpanded }) => (
  <Box
    element="button"
    type="button"
    aria-expanded={ariaExpanded}
    disabled={disabled}
    onClick={disabled ? undefined : onClick}
    sx={{
      minHeight: "36px",
      padding: "0 14px",
      border: `1px solid ${active ? DS.accent : DS.line}`,
      borderRadius: "10px",
      fontFamily: "inherit",
      fontSize: "13px",
      fontWeight: 600,
      whiteSpace: "nowrap",
      color: active ? DS.accentInk : DS.ink,
      backgroundColor: active ? DS.accentSoft : DS.surface,
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.55 : 1,
      transition: "background-color .15s ease",
      "&:hover:not(:disabled)": { backgroundColor: active ? DS.accentSoft : DS.soft },
      [MOBILE]: { minHeight: "44px", padding: "0 16px", fontSize: "14px" },
    }}
  >
    {children}
  </Box>
);

export interface TransactionsListProps {
  source?: "teams" | "global";
  sourceId?: string;
  initialCardUid?: string;
  /** Opens the list already filtered to one team (e.g. from team analytics). */
  initialTeamUid?: string;
  /** Overrides the default date range: the last N days. */
  initialLookbackDays?: number;
  /** Opens pre-filtered by status (e.g. from the dashboard card-spend status). */
  initialStatus?: string;
  /** Opens pre-filtered by operation type (e.g. "spent"). */
  initialTxType?: string;
  onCardIdChange?: (cardId?: string) => void;
}

export const TransactionsList: FC<TransactionsListProps> = ({
  source,
  sourceId,
  initialCardUid,
  initialTeamUid,
  initialLookbackDays,
  initialStatus,
  initialTxType,
  onCardIdChange,
}) => {
  const isGlobalSource = source !== "teams";
  const isPrivateAccount = useIsPrivateAccount();
  const activeIssuerCurrency = useOptionalIssuer()?.selectedIssuer?.currency;
  const { width: viewportWidth, height: viewportHeight } = useWindowSize();
  const isMobile = isMobileViewport(viewportWidth, viewportHeight);
  const [transactionsResponse, setTransactionsResponse] =
    useState<IAPIResponse<ICompanyTransactionsResponse>>();
  const [transactionsBusy, setTransactionsBusy] = useState(false);
  const [availableFilters, setAvailableFilters] =
    useState<ICompanyTransactionFiltersResponse>();
  const requestSequence = useRef(0);
  const [cardsResponse] = GD.REQ_CARDS.useRequest();
  const [profile] = GD.REQ_PROFILE.useRequest();
  const [usersResponse] = GD.REQ_USERS.useRequest();
  const [teamsResponse] = GD.REQ_TEAMS_LIST.useRequest();
  const [defaultDateRange] = useState(() => {
    const endDate = new Date();
    const startDate = new Date(endDate);
    if (initialLookbackDays) startDate.setDate(startDate.getDate() - initialLookbackDays);
    else startDate.setMonth(startDate.getMonth() - DEFAULT_LOOKBACK_MONTHS);
    return { startDate, endDate };
  });

  const createDefaultFilterState = (
    cardUid = "All",
  ): TransactionFilterState => ({
    search: "",
    cardUid,
    buyerUid: "All",
    teamUid: "All",
    cardMask: "",
    cardBin: "",
    direction: "All",
    status: "All",
    startDate: new Date(defaultDateRange.startDate),
    endDate: new Date(defaultDateRange.endDate),
    minAmount: "",
    maxAmount: "",
    currency: "All",
    entity: "all",
    txType: "All",
  });
  const [defaultFilterState] = useState(() => createDefaultFilterState());
  const [filter, setFilter] = useState<TransactionFilterState>(() => ({
    ...createDefaultFilterState(initialCardUid || "All"),
    teamUid: initialTeamUid || "All",
    status: initialStatus || "All",
    txType: initialTxType || "All",
  }));
  const [appliedPresetFilter, setAppliedPresetFilter] =
    useState<ICompanyTransactionFilter | null>(null);
  const [debouncedFilter, setDebouncedFilter] = useState(filter);
  const [allCardsReloadKey, setAllCardsReloadKey] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filtersExpanded, setFiltersExpanded] = useState(
    Boolean(initialCardUid || initialTeamUid || initialStatus || initialTxType),
  );
  const [exportBusy, setExportBusy] = useState(false);
  const [lastResolvedTransactions, setLastResolvedTransactions] = useState<
    TransactionsListRow[]
  >([]);

  const textFilterData = {
    search: filter.search,
    cardUid: filter.cardUid,
    buyerUid: filter.buyerUid,
    teamUid: filter.teamUid,
    cardMask: filter.cardMask,
    cardBin: filter.cardBin,
    status: filter.status,
    direction: filter.direction,
    currency: filter.currency,
    minAmount: filter.minAmount,
    maxAmount: filter.maxAmount,
    entity: filter.entity,
    txType: filter.txType,
  };
  const entityOptions = withDefaultFilterOption(availableFilters?.entity, {
    value: "all",
    label: "All transactions",
  });
  const transactionTypeOptions = withDefaultFilterOption(
    availableFilters?.tx_type,
    {
      value: "All",
      label: "All operation types",
    },
  );
  const directionOptions = withDefaultFilterOption(
    availableFilters?.direction,
    {
      value: "All",
      label: "All directions",
    },
  );
  const statusOptions = withDefaultFilterOption(availableFilters?.status, {
    value: "All",
    label: "All statuses",
  });

  const buyers = useMemo(
    () =>
      ((usersResponse?.data || []) as IUserSaved[])
        .filter((user) => user.company_role === USER_ROLE.BUYER)
        .map((user) => ({
          value: user.uid,
          label: user.name || user.email || user.uid,
        })),
    [usersResponse],
  );

  const teams = useMemo(
    () =>
      ((teamsResponse?.data || []) as ITeamSaved[]).map((team) => ({
        value: team.uid,
        label: team.name || team.uid,
      })),
    [teamsResponse],
  );

  const cards = useMemo(
    () =>
      ((cardsResponse?.data || []) as ICardSaved[]).map((card) => ({
        value: card.uid,
        label: card.name || card.mask || card.uid,
      })),
    [cardsResponse],
  );

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedFilter(filter),
      FILTER_DEBOUNCE_MS,
    );
    return () => clearTimeout(timeout);
  }, [filter]);

  useEffect(() => {
    let active = true;
    void GD.REQ_COMPANY_TRANSACTION_FILTERS.request()
      .then((response) => {
        if (active && response.data) setAvailableFilters(response.data);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!isGlobalSource) return;
    GD.REQ_TEAMS_LIST.request();
    GD.REQ_CARDS.request();
    if (profile?.company_role !== USER_ROLE.BUYER) GD.REQ_USERS.request();
  }, [isGlobalSource, profile?.company_role]);

  useEffect(() => {
    const nextCardUid = initialCardUid || "All";
    setFilter((previous) =>
      previous.cardUid === nextCardUid
        ? previous
        : { ...previous, cardUid: nextCardUid },
    );
  }, [initialCardUid]);

  useEffect(() => {
    if (initialCardUid) setFiltersExpanded(true);
  }, [initialCardUid]);

  const updateCardFilter = (nextCardUid: string) => {
    setAppliedPresetFilter(null);
    setFilter((previous) =>
      previous.cardUid === nextCardUid
        ? previous
        : { ...previous, cardUid: nextCardUid },
    );
    if (isGlobalSource && nextCardUid === "All") {
      setAllCardsReloadKey((previous) => previous + 1);
    }
    if (isGlobalSource && onCardIdChange) {
      onCardIdChange(nextCardUid !== "All" ? nextCardUid : undefined);
    }
  };

  const resetFilters = () => {
    setAppliedPresetFilter(null);
    setFilter(createDefaultFilterState());
    if (isGlobalSource && onCardIdChange) onCardIdChange(undefined);
  };

  const updateFilter = (
    updater: (previous: TransactionFilterState) => TransactionFilterState,
  ) => {
    setAppliedPresetFilter(null);
    setFilter(updater);
  };

  const selectedCardUid =
    isGlobalSource && filter.cardUid !== "All" ? filter.cardUid : undefined;
  const missingTeamSource = source === "teams" && !sourceId;

  const buildServerFilter = useCallback(
    (currentFilter: TransactionFilterState): ICompanyTransactionFilter => ({
      from: currentFilter.startDate
        ? formatApiDate(currentFilter.startDate)
        : undefined,
      // An open-ended period runs up to today.
      to: currentFilter.endDate || currentFilter.startDate
        ? formatApiDate(currentFilter.endDate ?? new Date())
        : undefined,
      status:
        currentFilter.status !== "All" ? currentFilter.status : undefined,
      direction:
        currentFilter.direction !== "All"
          ? (currentFilter.direction as "income" | "expense")
          : undefined,
      currency:
        currentFilter.currency !== "All"
          ? currentFilter.currency
          : undefined,
      search: currentFilter.search.trim() || undefined,
      min_amount: currentFilter.minAmount.trim()
        ? Number(currentFilter.minAmount)
        : undefined,
      max_amount: currentFilter.maxAmount.trim()
        ? Number(currentFilter.maxAmount)
        : undefined,
      ...(currentFilter.entity !== "all"
        ? { entity: currentFilter.entity as TCompanyTransactionEntity }
        : {}),
      tx_type:
        currentFilter.txType !== "All"
          ? (currentFilter.txType as TCompanyTransactionType | "any")
          : undefined,
      buyer_uid:
        isGlobalSource &&
        profile?.company_role !== USER_ROLE.BUYER &&
        currentFilter.buyerUid !== "All"
          ? currentFilter.buyerUid
          : undefined,
      team_uid:
        isGlobalSource && currentFilter.teamUid !== "All"
          ? currentFilter.teamUid
          : undefined,
      card_mask:
        isGlobalSource && currentFilter.cardMask
          ? currentFilter.cardMask
          : undefined,
      bin:
        isGlobalSource && currentFilter.cardBin
          ? currentFilter.cardBin
          : undefined,
    }),
    [isGlobalSource, profile?.company_role],
  );

  const serverFilter = useMemo(
    () => buildServerFilter(debouncedFilter),
    [buildServerFilter, debouncedFilter],
  );
  const effectiveServerFilter = useMemo(
    () => appliedPresetFilter ?? serverFilter,
    [appliedPresetFilter, serverFilter],
  );

  const applyPreset = (preset: ITransactionFilterPreset) => {
    const presetFilter = preset.filter;
    setAppliedPresetFilter(presetFilter);
    setFilter((previous) => ({
      ...previous,
      search: presetFilter.search || "",
      cardUid: presetFilter.card_uid || "All",
      buyerUid: presetFilter.buyer_uid || "All",
      teamUid: presetFilter.team_uid || "All",
      cardMask: presetFilter.card_mask || "",
      cardBin: presetFilter.bin || "",
      direction: presetFilter.direction || "All",
      status: presetFilter.status || "All",
      startDate: presetFilter.from ? new Date(`${presetFilter.from}T00:00:00`) : null,
      endDate: presetFilter.to ? new Date(`${presetFilter.to}T00:00:00`) : null,
      minAmount: presetFilter.min_amount === undefined ? "" : String(presetFilter.min_amount),
      maxAmount: presetFilter.max_amount === undefined ? "" : String(presetFilter.max_amount),
      currency: presetFilter.currency || "All",
      entity: presetFilter.entity || "all",
      txType: presetFilter.tx_type && presetFilter.tx_type !== "any" ? presetFilter.tx_type : "All",
    }));
    setCurrentPage(1);
    setFiltersExpanded(true);
    if (isGlobalSource && onCardIdChange) onCardIdChange(presetFilter.card_uid);
  };
  const presetCurrentFilter = useMemo<ICompanyTransactionFilter>(
    () => ({
      ...serverFilter,
      ...(selectedCardUid ? { card_uid: selectedCardUid } : {}),
      ...(source === "teams" && sourceId ? { team_uid: sourceId } : {}),
    }),
    [selectedCardUid, serverFilter, source, sourceId],
  );

  useEffect(() => {
    if (missingTeamSource) {
      requestSequence.current += 1;
      setTransactionsBusy(false);
      return;
    }

    const sequence = ++requestSequence.current;
    setTransactionsBusy(true);
    const requestFilter = {
      ...effectiveServerFilter,
      ...(source === "teams" ? { team_uid: sourceId } : {}),
    };
    const request = (pageFilter: ICompanyTransactionFilter) =>
      selectedCardUid && !appliedPresetFilter
        ? GD.REQ_COMPANY_TRANSACTIONS_CARD.request({
            card_uid: selectedCardUid,
            ...pageFilter,
          })
        : GD.REQ_COMPANY_TRANSACTIONS_ALL.request(pageFilter);

    request({
      ...requestFilter,
      page: currentPage - 1,
      limit: TRANSACTIONS_PAGE_LIMIT,
    })
      .then((response) => {
        if (sequence !== requestSequence.current) return;
        setTransactionsResponse(response);
        setTransactionsBusy(false);
      })
      .catch((error) => {
        if (sequence !== requestSequence.current) return;
        setTransactionsResponse({
          error: {
            code: -1,
            message: getFriendlyErrorMessage(
              error,
              "Could not load transactions",
            ),
          },
        });
        setTransactionsBusy(false);
      });

    return () => {
      if (sequence === requestSequence.current) requestSequence.current += 1;
    };
  }, [
    allCardsReloadKey,
    currentPage,
    missingTeamSource,
    selectedCardUid,
    effectiveServerFilter,
    appliedPresetFilter,
    source,
    sourceId,
  ]);

  const transactions = useMemo<TransactionsListRow[]>(
    () =>
      (transactionsResponse?.data?.transactions || []).map((transaction) => ({
        ...transaction,
        uid: String(transaction.id),
      })),
    [transactionsResponse],
  );
  const currencyOptions = useMemo(() => {
    const currencies = new Set<string>();
    if (activeIssuerCurrency?.trim()) currencies.add(activeIssuerCurrency.trim());
    ((cardsResponse?.data || []) as ICardSaved[]).forEach((card) => {
      if (card.currency?.trim()) currencies.add(card.currency.trim());
    });
    transactions.forEach((transaction) => {
      if (transaction.currency?.trim()) currencies.add(transaction.currency.trim());
    });
    if (currencies.size === 0) currencies.add(DEFAULT_CURRENCY);
    return [
      { value: "All", label: "All currencies" },
      ...Array.from(currencies, (currency) => ({ value: currency, label: currency })),
    ];
  }, [activeIssuerCurrency, cardsResponse, transactions]);

  useEffect(() => {
    if (transactions.length > 0) {
      setLastResolvedTransactions(transactions);
      return;
    }
    if (!transactionsBusy && transactionsResponse)
      setLastResolvedTransactions([]);
  }, [transactionsBusy, transactions, transactionsResponse]);

  useEffect(
    () => setCurrentPage(1),
    [debouncedFilter, selectedCardUid, source, sourceId],
  );

  const displayTransactions =
    transactionsBusy &&
    transactions.length === 0 &&
    lastResolvedTransactions.length > 0
      ? lastResolvedTransactions
      : transactions;
  const displayItems = useMemo(
    () => groupInternalTransfers(displayTransactions),
    [displayTransactions],
  );
  const pagination = transactionsResponse?.data?.pagination;
  const totalTransactions = Number(
    pagination?.total ?? displayTransactions.length,
  );
  const totalPages = Math.max(
    1,
    Number(
      pagination?.pages ??
        Math.ceil(totalTransactions / TRANSACTIONS_PAGE_LIMIT),
    ),
  );
  const visibleStart =
    totalTransactions === 0
      ? 0
      : (currentPage - 1) * TRANSACTIONS_PAGE_LIMIT + 1;
  const visibleEnd = Math.min(
    visibleStart + displayTransactions.length - 1,
    totalTransactions,
  );
  const hasTransactions = displayItems.length > 0;
  const transactionError = missingTeamSource
    ? { code: 0, message: "Team is required to load transactions." }
    : transactionsResponse?.error;
  const totals = transactionsResponse?.data?.totals ?? {};
  const totalsEntries = Object.entries(totals);
  const accountHolderScope =
    ACCOUNT_HOLDER_SCOPE_LABELS[filter.entity as TCompanyTransactionEntity] ??
    "All";
  const tableColumns = isGlobalSource
    ? "104px minmax(0, 2.2fr) minmax(0, 1.3fr) 132px 112px"
    : "104px minmax(0, 2.2fr) 132px 112px";
  const tableMinWidth = isGlobalSource ? "820px" : "640px";

  const hasActiveFilters =
    filter.search !== defaultFilterState.search ||
    filter.cardUid !== defaultFilterState.cardUid ||
    filter.buyerUid !== defaultFilterState.buyerUid ||
    filter.teamUid !== defaultFilterState.teamUid ||
    filter.cardMask !== defaultFilterState.cardMask ||
    filter.cardBin !== defaultFilterState.cardBin ||
    filter.direction !== defaultFilterState.direction ||
    filter.status !== defaultFilterState.status ||
    (filter.startDate?.getTime() ?? null) !==
      (defaultFilterState.startDate?.getTime() ?? null) ||
    (filter.endDate?.getTime() ?? null) !==
      (defaultFilterState.endDate?.getTime() ?? null) ||
    filter.minAmount !== defaultFilterState.minAmount ||
    filter.maxAmount !== defaultFilterState.maxAmount ||
    filter.currency !== defaultFilterState.currency ||
    filter.entity !== defaultFilterState.entity ||
    filter.txType !== defaultFilterState.txType;

  const activeFilterCount = (Object.keys(filter) as (keyof TransactionFilterState)[])
    .filter((key) => key !== "search" &&
      (filter[key] instanceof Date ? filter[key].getTime() : filter[key]) !==
      (defaultFilterState[key] instanceof Date ? defaultFilterState[key].getTime() : defaultFilterState[key]))
    .length;

  const exportTransactions = async () => {
    if (exportBusy || missingTeamSource) return;
    setExportBusy(true);
    try {
      const exportFilter = {
        ...buildServerFilter(filter),
        ...(source === "teams" ? { team_uid: sourceId } : {}),
        no_limit: true,
      };
      const response = selectedCardUid
        ? await GD.REQ_COMPANY_TRANSACTIONS_CARD.request({
            card_uid: selectedCardUid,
            ...exportFilter,
          })
        : await GD.REQ_COMPANY_TRANSACTIONS_ALL.request(exportFilter);
      if (response.error) throw response.error;
      if (response.data.truncated) {
        toast.error(
          "The export exceeds 50,000 rows. Narrow the date range before exporting.",
        );
        return;
      }

      const csv = createTransactionsCsv(response.data.transactions);
      const blob = new Blob(["\uFEFF", csv], {
        type: "text/csv;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `transactions-${new Date().toISOString().slice(0, ISO_DATE_LENGTH)}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(
        getFriendlyErrorMessage(error, "Could not export transactions."),
      );
    } finally {
      setExportBusy(false);
    }
  };

  const openTransaction = (item: TransactionListItem<TransactionsListRow>) => {
    if (item.kind === "internal-transfer") {
      S_UNIVERSAL_DIALOG.invoke({
        label: "Transfer Details",
        closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
        sx: { maxWidth: "640px", width: "100%" },
        content: <InternalTransferDetails entries={item.ledgerEntries} />,
      });
      return;
    }

    const transaction = item.transaction;
    const typeOption = availableFilters?.tx_type.find(
      (option) => option.value === transaction.operation_type,
    );
    S_UNIVERSAL_DIALOG.invoke({
      label: "Transaction Details",
      closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
      sx: { maxWidth: "600px", width: "100%" },
      content: (
        <TransactionDetails transaction={transaction} typeOption={typeOption} />
      ),
    });
  };

  // Filters wait for the debounce before the request starts; show progress from the first change.
  const refreshing = (transactionsBusy || filter !== debouncedFilter) && hasTransactions;
  const entriesLabel =
    refreshing
      ? "Updating…"
      : `${totalTransactions.toLocaleString("en-GB")} ${totalTransactions === 1 ? "entry" : "entries"}`;
  const showQuickSelects = !isMobile || filtersExpanded;

  const renderRow = (
    item: TransactionListItem<TransactionsListRow>,
  ) => {
    const transaction = item.transaction;
    const isInternalTransfer = item.kind === "internal-transfer";
    const operationId = transaction.operation_id ?? transaction.id;
    const typeOption = availableFilters?.tx_type.find(
      (option) => option.value === transaction.operation_type,
    );
    const operationTypeLabel = isInternalTransfer
      ? "Internal transfer"
      : getTransactionTypeLabel(transaction, typeOption);
    const description = getTransactionDisplayLabel(transaction, typeOption, isPrivateAccount);
    const parties = getTransactionParties(transaction, isPrivateAccount);
    const amountPresentation = getTransactionAmountPresentation(transaction);
    const amountText = `${isInternalTransfer ? "" : amountPresentation.sign}${formatCurrencyWithNumberToSymbolWithNumber(
      amountPresentation.amount,
      transaction.currency,
    )}`;
    const flowText = `${parties.from} → ${parties.to}`;
    const referenceText = `${operationTypeLabel} · Operation #${operationId}${
      isInternalTransfer
        ? " · 2 ledger entries"
        : transaction.parent_trade_no
          ? ` · Parent ${transaction.parent_trade_no}`
          : ""
    }`;
    const feeNotes = getFeeNotes(transaction);
    const buyerText = transaction.buyer_name || "—";
    const cardText = `${transaction.team_name || "No team"} · ${getMaskedLastFour(transaction.card_mask)}${
      transaction.card_bin ? ` · BIN ${transaction.card_bin}` : ""
    }`;
    const amountColor = isInternalTransfer ? DS.ink : amountPresentation.color;
    const dateText = formatDate(transaction.created_at);
    const timeText = formatTime(transaction.created_at);

    const identity = (
      <HBox gap="12px" sx={{ alignItems: "center", minWidth: 0 }}>
        <DirectionTile
          kind={isInternalTransfer ? "transfer" : transaction.direction}
        />
        <VBox gap="2px" sx={{ minWidth: 0, flex: "1 1 0" }}>
          <Typo sx={{ ...ELLIPSIS_SX, color: DS.ink, fontSize: "14px", fontWeight: 650 }}>
            {description}
          </Typo>
          <Typo sx={{ ...ELLIPSIS_SX, color: DS.muted, fontSize: "13px" }}>
            {flowText}
          </Typo>
          <Typo sx={{ ...ELLIPSIS_SX, color: DS.faint, fontSize: "12px" }}>
            {referenceText}
          </Typo>
          {feeNotes.length > 0 && (
            <Typo sx={{ ...ELLIPSIS_SX, color: DS.faint, fontSize: "12px" }}>
              {feeNotes.join(" · ")}
            </Typo>
          )}
        </VBox>
      </HBox>
    );

    const rowProps = {
      role: "button",
      "aria-label": `Open ${isInternalTransfer ? "transfer" : "transaction"} details: ${description}`,
      title: `Open ${isInternalTransfer ? "transfer" : "transaction"} details`,
      tabIndex: 0,
      onClick: () => openTransaction(item),
      onKeyDown: (event: ReactKeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openTransaction(item);
        }
      },
    };
    const interactiveSx = {
      cursor: "pointer",
      transition: "background-color .15s ease",
      "&:hover": { backgroundColor: DS.soft },
      "&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "-2px" },
    };

    if (isMobile) {
      return (
        <Box
          key={item.key}
          {...rowProps}
          sx={{
            ...interactiveSx,
            margin: "10px 10px 0",
            padding: "14px",
            border: `1px solid ${DS.line}`,
            borderRadius: "14px",
            backgroundColor: DS.surface,
            boxShadow: DS.shadow,
          }}
        >
          <VBox gap="10px">
            <HBox gap="12px" sx={{ alignItems: "flex-start", justifyContent: "space-between" }}>
              <Box sx={{ minWidth: 0, flex: "1 1 0" }}>{identity}</Box>
              <Typo
                sx={{
                  color: amountColor,
                  fontSize: "15px",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {amountText}
              </Typo>
            </HBox>
            <HBox gap="8px" sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
              <Typo sx={{ color: DS.muted, fontSize: "13px" }}>
                {dateText} · {timeText}
              </Typo>
              <StatusBadge status={transaction.status} />
            </HBox>
            {isGlobalSource && (
              <Typo sx={{ color: DS.muted, fontSize: "13px", overflowWrap: "anywhere" }}>
                {buyerText} · {cardText}
              </Typo>
            )}
          </VBox>
        </Box>
      );
    }

    return (
      <Box
        key={item.key}
        {...rowProps}
        sx={{
          ...interactiveSx,
          display: "grid",
          gridTemplateColumns: tableColumns,
          alignItems: "center",
          columnGap: "16px",
          minHeight: "72px",
          minWidth: tableMinWidth,
          padding: "12px 20px",
          borderTop: `1px solid ${DS.line}`,
        }}
      >
        <VBox gap="2px">
          <Typo sx={{ color: DS.ink, fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap" }}>
            {dateText}
          </Typo>
          <Typo sx={{ color: DS.muted, fontSize: "12px" }}>{timeText}</Typo>
        </VBox>
        {identity}
        {isGlobalSource && (
          <VBox gap="2px" sx={{ minWidth: 0 }}>
            <Typo sx={{ ...ELLIPSIS_SX, color: DS.ink, fontSize: "13px", fontWeight: 600 }}>
              {buyerText}
            </Typo>
            <Typo sx={{ ...ELLIPSIS_SX, color: DS.muted, fontSize: "12px" }}>
              {cardText}
            </Typo>
          </VBox>
        )}
        <Typo
          sx={{
            color: amountColor,
            fontSize: "14px",
            fontWeight: 700,
            textAlign: "right",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {amountText}
        </Typo>
        <HBox>
          <StatusBadge status={transaction.status} />
        </HBox>
      </Box>
    );
  };

  return (
    <VBox gap="16px" sx={{ minHeight: 0 }}>
      {totalsEntries.map(([currency, values]) => (
        <VBox key={currency} gap="8px">
          <Typo sx={{ color: DS.muted, fontSize: "13px", fontWeight: 500 }}>
            {currency} · {accountHolderScope}
          </Typo>
          <StatGroup
            items={[
              {
                label: "Turnover",
                value: formatCurrencyWithNumberToSymbolWithNumber(
                  values.spent ?? values.settled,
                  currency,
                ),
              },
              {
                label: "Pending",
                value: formatCurrencyWithNumberToSymbolWithNumber(
                  values.pending,
                  currency,
                ),
              },
              {
                label: "Commission",
                value: formatCurrencyWithNumberToSymbolWithNumber(
                  values.commission,
                  currency,
                ),
              },
              {
                label: "Returned",
                value: formatCurrencyWithNumberToSymbolWithNumber(
                  values.returned ?? 0,
                  currency,
                ),
              },
            ]}
          />
        </VBox>
      ))}

      {/* ─── Toolbar ─────────────────────────────────────────────── */}
      <VBox
        gap="0px"
        sx={{
          backgroundColor: DS.surface,
          borderRadius: DS.radius,
          border: `1px solid ${DS.line}`,
          boxShadow: DS.shadow,
          overflow: "hidden",
        }}
      >
        <HBox
          gap="10px"
          sx={{ alignItems: "center", flexWrap: "wrap", padding: "14px 16px" }}
        >
          <Box
            role="search"
            aria-label="Search transactions"
            sx={{ flex: "1 1 280px", minWidth: 0 }}
          >
            <Input
              placeholder="Search description, buyer, card or operation ID"
              data={textFilterData}
              name="search"
              size="small"
              iconLeft={<SearchIcon />}
              onChange={(value) =>
                updateFilter((previous) => ({ ...previous, search: value }))
              }
              sx={{ width: "100%" }}
            />
          </Box>
          {showQuickSelects && (
            <>
              <Box sx={{ flex: "0 1 160px", minWidth: "130px", [MOBILE]: { flex: "1 1 140px" } }}>
                <Select
                  ariaLabel="Status"
                  values={statusOptions}
                  data={textFilterData}
                  name="status"
                  size="small"
                  onChange={(value) =>
                    updateFilter((previous) => ({ ...previous, status: value as string }))
                  }
                  sx={{ width: "100%" }}
                />
              </Box>
              <Box sx={{ flex: "0 1 160px", minWidth: "130px", [MOBILE]: { flex: "1 1 140px" } }}>
                <Select
                  ariaLabel="Direction"
                  values={directionOptions}
                  data={textFilterData}
                  name="direction"
                  size="small"
                  onChange={(value) =>
                    updateFilter((previous) => ({ ...previous, direction: value as string }))
                  }
                  sx={{ width: "100%" }}
                />
              </Box>
              <Box sx={{ flex: "0 1 150px", minWidth: "120px", [MOBILE]: { flex: "1 1 140px" } }}>
                <Select
                  ariaLabel="Currency"
                  values={currencyOptions}
                  data={textFilterData}
                  name="currency"
                  size="small"
                  onChange={(value) =>
                    updateFilter((previous) => ({ ...previous, currency: value as string }))
                  }
                  sx={{ width: "100%" }}
                />
              </Box>
            </>
          )}
          <HBox
            gap="8px"
            sx={{
              marginLeft: "auto",
              alignItems: "center",
              [MOBILE]: { marginLeft: 0, width: "100%", flexWrap: "wrap" },
            }}
          >
            {hasActiveFilters && (
              <ToolbarButton onClick={resetFilters}>Reset</ToolbarButton>
            )}
            <ToolbarButton
              active={filtersExpanded}
              ariaExpanded={filtersExpanded}
              onClick={() => setFiltersExpanded((previous) => !previous)}
            >
              {filtersExpanded ? "Hide filters" : "More filters"}
              {isMobile && activeFilterCount > 0 ? ` · ${activeFilterCount}` : ""}
            </ToolbarButton>
            <ToolbarButton
              disabled={exportBusy || missingTeamSource}
              onClick={() => void exportTransactions()}
            >
              {exportBusy ? "Exporting…" : "Export CSV"}
            </ToolbarButton>
          </HBox>
        </HBox>

        {isGlobalSource && showQuickSelects && (
          <Box
            sx={{
              padding: "10px 16px 12px",
              borderTop: `1px solid ${DS.line}`,
              backgroundColor: DS.soft,
            }}
          >
            <TransactionFilterPresets
              datePeriodOptions={availableFilters?.date_period}
              currentFilter={presetCurrentFilter}
              active={appliedPresetFilter !== null}
              onApply={applyPreset}
            />
          </Box>
        )}

        {filtersExpanded && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isGlobalSource
                ? "repeat(6, minmax(0, 1fr))"
                : "repeat(4, minmax(0, 1fr))",
              columnGap: "12px",
              rowGap: "12px",
              padding: "14px 16px 16px",
              borderTop: `1px solid ${DS.line}`,
              animation: "v2-enter .24s ease both",
              "@media (max-width: 1280px)": {
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              },
              "@media (max-width: 600px)": {
                gridTemplateColumns: "minmax(0, 1fr)",
                padding: "12px",
              },
            }}
          >
            {isGlobalSource && profile?.company_role !== USER_ROLE.BUYER && (
              <AdvancedFilterField label="Buyer" order={1}>
                <Select
                  values={[{ value: "All", label: "All buyers" }, ...buyers]}
                  data={textFilterData}
                  name="buyerUid"
                  size="small"
                  onChange={(value) =>
                    updateFilter((previous) => ({ ...previous, buyerUid: value as string }))
                  }
                  sx={{ width: "100%" }}
                />
              </AdvancedFilterField>
            )}
            {isGlobalSource && (
              <AdvancedFilterField label="Team" order={2}>
                <Select
                  values={[{ value: "All", label: "All teams" }, ...teams]}
                  data={textFilterData}
                  name="teamUid"
                  size="small"
                  onChange={(value) =>
                    updateFilter((previous) => ({ ...previous, teamUid: value as string }))
                  }
                  sx={{ width: "100%" }}
                />
              </AdvancedFilterField>
            )}
            {isGlobalSource && (
              <AdvancedFilterField label="Card" order={3}>
                <Select
                  values={[{ value: "All", label: "All cards" }, ...cards]}
                  data={textFilterData}
                  name="cardUid"
                  size="small"
                  onChange={(value) => updateCardFilter(value as string)}
                  sx={{ width: "100%" }}
                />
              </AdvancedFilterField>
            )}
            <AdvancedFilterField label="From" order={8}>
              <DatePicker
                ariaLabel="From date"
                size="small"
                min={null}
                value={filter.startDate || undefined}
                onChange={(value) =>
                  updateFilter((previous) => {
                    const startDate = toDateValue(value);
                    // Keep the period valid: moving From past To drags To along.
                    const endDate =
                      startDate && previous.endDate && startDate > previous.endDate
                        ? startDate
                        : previous.endDate;
                    return { ...previous, startDate, endDate };
                  })
                }
              />
            </AdvancedFilterField>
            <AdvancedFilterField label="To" order={9}>
              <DatePicker
                ariaLabel="To date"
                size="small"
                min={filter.startDate || undefined}
                value={filter.endDate || undefined}
                onChange={(value) =>
                  updateFilter((previous) => ({
                    ...previous,
                    endDate: toDateValue(value),
                  }))
                }
              />
            </AdvancedFilterField>
            {isGlobalSource && (
              <AdvancedFilterField label="BIN" order={4}>
                <Input
                  placeholder="Any BIN"
                  data={textFilterData}
                  name="cardBin"
                  size="small"
                  onChange={(value) =>
                    updateFilter((previous) => ({
                      ...previous,
                      cardBin: normalizeDigits(value),
                    }))
                  }
                  sx={ADVANCED_FILTER_INPUT_SX}
                />
              </AdvancedFilterField>
            )}
            {isGlobalSource && (
              <AdvancedFilterField label="Card last 4" order={5}>
                <Input
                  placeholder="Any last 4"
                  data={textFilterData}
                  name="cardMask"
                  size="small"
                  onChange={(value) =>
                    updateFilter((previous) => ({
                      ...previous,
                      cardMask: normalizeDigits(value, CARD_LAST_FOUR_LENGTH),
                    }))
                  }
                  sx={ADVANCED_FILTER_INPUT_SX}
                />
              </AdvancedFilterField>
            )}
            <AdvancedFilterField label="Min amount" order={10}>
              <Input
                placeholder="No minimum"
                data={textFilterData}
                name="minAmount"
                type="number"
                size="small"
                onChange={(value) =>
                  updateFilter((previous) => ({ ...previous, minAmount: value }))
                }
                sx={ADVANCED_FILTER_INPUT_SX}
              />
            </AdvancedFilterField>
            <AdvancedFilterField label="Max amount" order={11}>
              <Input
                placeholder="No maximum"
                data={textFilterData}
                name="maxAmount"
                type="number"
                size="small"
                onChange={(value) =>
                  updateFilter((previous) => ({ ...previous, maxAmount: value }))
                }
                sx={ADVANCED_FILTER_INPUT_SX}
              />
            </AdvancedFilterField>
            {isGlobalSource && (
              <AdvancedFilterField label="Account holder" order={6}>
                <Select
                  values={entityOptions}
                  data={textFilterData}
                  name="entity"
                  size="small"
                  onChange={(value) =>
                    updateFilter((previous) => ({ ...previous, entity: value as string }))
                  }
                  sx={{ width: "100%" }}
                />
              </AdvancedFilterField>
            )}
            {isGlobalSource && (
              <AdvancedFilterField label="Operation type" order={7}>
                <Select
                  values={transactionTypeOptions}
                  data={textFilterData}
                  name="txType"
                  size="small"
                  onChange={(value) =>
                    updateFilter((previous) => ({ ...previous, txType: value as string }))
                  }
                  sx={{ width: "100%" }}
                />
              </AdvancedFilterField>
            )}
          </Box>
        )}
      </VBox>

      {/* ─── Table ───────────────────────────────────────────────── */}
      <Box
        sx={{
          backgroundColor: DS.surface,
          borderRadius: DS.radius,
          border: `1px solid ${DS.line}`,
          boxShadow: DS.shadow,
          overflow: "hidden",
          minHeight: "220px",
        }}
      >
        <HBox
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 20px 12px",
          }}
        >
          <Typo sx={{ color: DS.muted, fontSize: "13px" }}>{entriesLabel}</Typo>
        </HBox>
        <ProgressBar busy={refreshing} color={DS.accent} label="Updating transactions" />
        {transactionsBusy && !hasTransactions ? (
          <VBox center sx={{ minHeight: "260px" }} gap="10px">
            <Spinner />
            <Typo sx={{ color: DS.muted, fontSize: "13px" }}>
              Loading transactions…
            </Typo>
          </VBox>
        ) : transactionError ? (
          <VBox center sx={{ minHeight: "240px", padding: "30px" }} gap="8px">
            <Typo sx={{ color: "#B42318", fontWeight: 650, fontSize: "15px" }}>
              Could not load transactions
            </Typo>
            <Typo sx={{ color: DS.muted, fontSize: "13px" }}>
              {getFriendlyErrorMessage(transactionError)}
            </Typo>
            <Button
              variant="secondarySmall"
              onClick={() => setAllCardsReloadKey((previous) => previous + 1)}
            >
              Retry
            </Button>
          </VBox>
        ) : !hasTransactions ? (
          <VBox center sx={{ minHeight: "240px", padding: "30px" }} gap="12px">
            <Box
              aria-hidden
              sx={{
                width: "52px",
                height: "52px",
                borderRadius: "16px",
                background: DS.accentSoft,
                color: DS.accent,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 4v16M7 20l-3-3M7 20l3-3" />
                <path d="M17 20V4M17 4l-3 3M17 4l3 3" />
              </svg>
            </Box>
            <VBox gap="4px" sx={{ alignItems: "center" }}>
              <Typo sx={{ color: DS.ink, fontWeight: 650, fontSize: "15px" }}>
                No transactions found
              </Typo>
              <Typo sx={{ color: DS.muted, fontSize: "13px" }}>
                Try changing the filters.
              </Typo>
            </VBox>
          </VBox>
        ) : (
          <>
            <Box sx={{ overflowX: isMobile ? "hidden" : "auto", paddingBottom: isMobile ? "10px" : 0, opacity: refreshing ? 0.5 : 1, transition: "opacity .15s", pointerEvents: refreshing ? "none" : undefined }}>
              {!isMobile && (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: tableColumns,
                    alignItems: "center",
                    columnGap: "16px",
                    padding: "10px 20px",
                    minWidth: tableMinWidth,
                    backgroundColor: DS.soft,
                    borderTop: `1px solid ${DS.line}`,
                  }}
                >
                  <TransactionHeaderCell>Date</TransactionHeaderCell>
                  <TransactionHeaderCell>Description</TransactionHeaderCell>
                  {isGlobalSource && (
                    <TransactionHeaderCell>Buyer / card</TransactionHeaderCell>
                  )}
                  <TransactionHeaderCell align="right">Amount</TransactionHeaderCell>
                  <TransactionHeaderCell>Status</TransactionHeaderCell>
                </Box>
              )}
              {displayItems.map(renderRow)}
            </Box>

            <HBox
              gap="10px"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 20px",
                flexWrap: "wrap",
                borderTop: `1px solid ${DS.line}`,
              }}
            >
              <Typo sx={{ color: DS.muted, fontSize: "13px" }}>
                Showing {visibleStart}–{visibleEnd} of {totalTransactions}
              </Typo>
              {totalPages > 1 && (
                <HBox gap="8px" sx={{ alignItems: "center" }}>
                  <Button
                    variant="secondarySmall"
                    disabled={currentPage === 1}
                    onClick={() =>
                      setCurrentPage((previous) => Math.max(1, previous - 1))
                    }
                    sx={{ padding: "6px 12px" }}
                  >
                    Previous
                  </Button>
                  <Typo sx={{ color: DS.ink, fontSize: "13px", fontWeight: 600 }}>
                    Page {currentPage} of {totalPages}
                  </Typo>
                  <Button
                    variant="secondarySmall"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      setCurrentPage((previous) =>
                        Math.min(totalPages, previous + 1),
                      )
                    }
                    sx={{ padding: "6px 12px" }}
                  >
                    Next
                  </Button>
                </HBox>
              )}
            </HBox>
          </>
        )}
      </Box>
    </VBox>
  );
};
